import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { Shield, Heart, Users, MapPin, ArrowRight, Star, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, useSpring, useMotionValue, useTransform } from "framer-motion";

/* ─── Mesh gradient that follows cursor ─── */
function CursorMesh({ x, y }: { x: number; y: number }) {
  const sx = useSpring(useMotionValue(x * 100), { stiffness: 40, damping: 25 });
  const sy = useSpring(useMotionValue(y * 100), { stiffness: 40, damping: 25 });

  // update springs
  sx.set(x * 100);
  sy.set(y * 100);

  return (
    <>
      {/* Primary orb */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--primary) / 0.18) 0%, hsl(var(--secondary) / 0.10) 40%, transparent 70%)",
          left: useTransform(sx, (v) => `${v}%`),
          top: useTransform(sy, (v) => `${v}%`),
          translateX: "-50%",
          translateY: "-50%",
          filter: "blur(40px)",
        }}
      />
      {/* Secondary orb – offset */}
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--secondary) / 0.22) 0%, hsl(var(--tertiary) / 0.08) 50%, transparent 70%)",
          left: useTransform(sx, (v) => `${v + 12}%`),
          top: useTransform(sy, (v) => `${v - 8}%`),
          translateX: "-50%",
          translateY: "-50%",
          filter: "blur(30px)",
        }}
      />
    </>
  );
}

/* ─── Floating geometric shapes ─── */
function FloatingShapes({ mx, my }: { mx: number; my: number }) {
  const shapes = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 20 + 8,
        rotation: Math.random() * 360,
        type: i % 3, // 0=circle, 1=square, 2=diamond
        hue: i % 2 === 0 ? "var(--secondary)" : "var(--primary)",
        dur: Math.random() * 20 + 12,
        delay: Math.random() * 6,
      })),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map((s) => {
        const px = (mx - 0.5) * 20 * (s.size / 28);
        const py = (my - 0.5) * 20 * (s.size / 28);
        const borderRadius = s.type === 0 ? "50%" : s.type === 2 ? "4px" : "3px";
        const rotate = s.type === 2 ? 45 : 0;

        return (
          <motion.div
            key={s.id}
            className="absolute"
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: s.size,
              height: s.size,
              borderRadius,
              border: `1.5px solid hsl(${s.hue} / 0.25)`,
              background: `hsl(${s.hue} / 0.06)`,
              rotate: rotate + s.rotation,
              x: px,
              y: py,
            }}
            animate={{
              y: [py, py - 30, py],
              opacity: [0.15, 0.4, 0.15],
              rotate: [rotate + s.rotation, rotate + s.rotation + 90, rotate + s.rotation],
            }}
            transition={{
              duration: s.dur,
              repeat: Infinity,
              delay: s.delay,
              ease: "easeInOut",
            }}
          />
        );
      })}
    </div>
  );
}

/* ─── Animations ─── */
const stagger = {
  hidden: { opacity: 0, y: 30 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const cardHover = {
  rest: { scale: 1, y: 0 },
  hover: { scale: 1.03, y: -6, transition: { duration: 0.25, ease: "easeOut" as const } },
};

export default function ConviteAgentePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [tokenStatus, setTokenStatus] = useState<'valid' | 'used' | 'invalid' | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Captura token da URL se presente
  const inviteToken = searchParams.get('token');

  useEffect(() => {
    if (inviteToken) {
      // Validação temporária usando localStorage (substituir por chamada ao backend)
      const usedTokens = JSON.parse(localStorage.getItem('usedInviteTokens') || '[]');
      
      if (usedTokens.includes(inviteToken)) {
        // Redireciona imediatamente para página de erro
        navigate('/convite-usado', { replace: true });
      } else {
        setTokenStatus('valid');
        
        // TODO: Validar token no backend
        // const validateToken = async () => {
        //   try {
        //     const response = await api.get(`v1/convites/agentes/${inviteToken}`);
        //     if (response.data.usedAt) {
        //       navigate('/convite-usado', { replace: true });
        //     } else {
        //       setTokenStatus('valid');
        //     }
        //   } catch (error) {
        //     setTokenStatus('invalid');
        //     navigate('/convite-usado', { replace: true });
        //   }
        // };
        // validateToken();
      }
    }
  }, [inviteToken, navigate]);

  const onMove = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setMouse({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  }, []);

  /**
   * Marca o token como usado e redireciona para cadastro
   */
  const handleAcceptInvite = useCallback(() => {
    if (inviteToken) {
      // Marca token como usado no localStorage (substituir por chamada ao backend)
      const usedTokens = JSON.parse(localStorage.getItem('usedInviteTokens') || '[]');
      if (!usedTokens.includes(inviteToken)) {
        usedTokens.push(inviteToken);
        localStorage.setItem('usedInviteTokens', JSON.stringify(usedTokens));
      }
      
      // TODO: Marcar token como usado no backend
      // await api.post(`v1/convites/agentes/${inviteToken}/aceitar`);
    }
    
    // Redireciona para cadastro
    navigate("/cadastro-agente");
  }, [inviteToken, navigate]);

  const values = [
    {
      icon: Users,
      title: "Ouvir o Cidadão",
      desc: "Cada solicitação é a voz de alguém que quer uma cidade melhor. Você será a ponte entre o cidadão e a solução.",
      gradient: "from-primary/20 to-secondary/10",
      iconColor: "text-primary",
    },
    {
      icon: Zap,
      title: "Transformar sua Cidade",
      desc: "Cada demanda resolvida é um passo para uma cidade mais justa, organizada e acolhedora para todos.",
      gradient: "from-secondary/20 to-tertiary/10",
      iconColor: "text-secondary",
    },
    {
      icon: Heart,
      title: "Fazer a Diferença",
      desc: "Seu trabalho impacta diretamente a vida de milhares de pessoas. Cada ação conta.",
      gradient: "from-tertiary/20 to-primary/10",
      iconColor: "text-tertiary",
    },
  ];

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      className="min-h-screen bg-background flex flex-col relative overflow-hidden"
    >
      {/* ── BG layers ── */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/8 via-background to-primary/5" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--secondary)/0.14),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.10),transparent_50%)]" />
        <CursorMesh x={mouse.x} y={mouse.y} />
        <FloatingShapes mx={mouse.x} my={mouse.y} />
      </div>

      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* ── Content ── */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Hero */}
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-16 text-center">
          <motion.div
            custom={0}
            variants={stagger}
            initial="hidden"
            animate="show"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-secondary/30 bg-secondary/10 text-secondary text-sm font-semibold mb-8 backdrop-blur-md"
          >
            <Sparkles className="h-4 w-4" />
            Convite Especial
          </motion.div>

          <motion.h1
            custom={1}
            variants={stagger}
            initial="hidden"
            animate="show"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1] mb-6"
          >
            Seja um agente que vai{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                fazer a diferença
              </span>
              <motion.span
                className="absolute -bottom-1.5 left-0 right-0 h-[3px] rounded-full bg-gradient-to-r from-primary to-secondary"
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.9, duration: 0.8, ease: "easeOut" }}
              />
            </span>{" "}
            na sua cidade
          </motion.h1>

          <motion.p
            custom={2}
            variants={stagger}
            initial="hidden"
            animate="show"
            className="text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed"
          >
            Você foi convidado para integrar a equipe da Ouvidoria Municipal.
            Juntos, vamos ouvir o cidadão e transformar a nossa cidade em um
            lugar melhor para todos.
          </motion.p>

          <motion.div custom={3} variants={stagger} initial="hidden" animate="show">
            <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.96 }}>
              <Button
                size="lg"
                className="gap-2 text-base px-10 h-14 rounded-full shadow-xl bg-gradient-to-r from-primary to-secondary hover:shadow-secondary/25 hover:shadow-2xl transition-shadow"
                onClick={handleAcceptInvite}
                disabled={tokenStatus === 'used' || tokenStatus === 'invalid'}
              >
                {tokenStatus === 'used' ? 'Convite Já Utilizado' : 'Aceitar Convite'}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Values */}
        <div className="max-w-4xl mx-auto px-6 pb-20 w-full">
          <motion.h2
            custom={4}
            variants={stagger}
            initial="hidden"
            animate="show"
            className="font-heading text-2xl font-semibold text-center text-foreground mb-10"
          >
            Como agente, você irá...
          </motion.h2>

          <div className="grid gap-6 sm:grid-cols-3">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                custom={i + 5}
                variants={stagger}
                initial="hidden"
                animate="show"
                whileHover="hover"
              >
                <motion.div
                  variants={cardHover}
                  className={`relative p-6 text-center rounded-2xl border border-border/40 backdrop-blur-md bg-card/60 overflow-hidden cursor-default group`}
                >
                  {/* Card gradient accent */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${v.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />

                  <div className="relative z-10">
                    <div
                      className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${v.gradient} flex items-center justify-center mx-auto mb-4 border border-border/30`}
                    >
                      <v.icon className={`h-7 w-7 ${v.iconColor}`} />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      {v.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {v.desc}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="relative z-10 border-y border-secondary/15 bg-gradient-to-r from-secondary/5 via-primary/5 to-secondary/5 backdrop-blur-sm"
        >
          <div className="max-w-2xl mx-auto px-6 py-12 text-center">
            <Star className="h-8 w-8 text-secondary/50 mx-auto mb-4" />
            <blockquote className="text-xl font-heading font-medium text-foreground italic leading-relaxed mb-4">
              "Uma cidade melhor começa quando cada um de nós decide fazer parte
              da mudança."
            </blockquote>
            <p className="text-sm text-muted-foreground">Equipe UrbNet</p>
          </div>
        </motion.div>

        {/* CTA final */}
        <div className="relative z-10 max-w-3xl mx-auto px-6 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
          >
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
              Pronto para começar?
            </h2>
            <p className="text-muted-foreground mb-8">
              Clique no botão abaixo para criar sua conta e fazer parte da equipe.
            </p>
            <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.96 }}>
              <Button
                size="lg"
                className="gap-2 px-10 h-14 rounded-full shadow-xl bg-gradient-to-r from-secondary to-primary hover:shadow-secondary/25 hover:shadow-2xl transition-shadow"
                onClick={handleAcceptInvite}
                disabled={tokenStatus === 'used' || tokenStatus === 'invalid'}
              >
                {tokenStatus === 'used' ? 'Convite Já Utilizado' : 'Criar Minha Conta'}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="relative z-10 border-t border-border py-6 text-center">
          <p className="text-xs text-muted-foreground">
            UrbNet &mdash; Tecnologia para cidades mais humanas
          </p>
        </footer>
      </div>
    </div>
  );
}
